import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared scaffolding for the four Strategy shells (Market / Clinical /
 * Technology / Milestones). Handles:
 *   • user + companyProfile fetch on mount
 *   • URL `?tab=` sync into activeTab
 *   • save / generate-card / progress-fetch HTTP calls (endpoint prefix driven)
 *   • common UI state (loading, saving, followups, card, cardOpen, generatingCard)
 *
 * Shell-specific state (slice buckets, EMPTY templates, payload shape) still
 * lives in each shell — this hook only owns what is truly identical.
 *
 * @param {object} opts
 * @param {string} opts.endpointKey   e.g. "market" / "clinical" / "technology" / "milestones"
 * @param {string} opts.defaultTab    default active tab id when no URL tab param
 * @param {Set<string>} opts.validTabs allowed tab ids for this shell
 */
export function useStrategyModule({ endpointKey, defaultTab, validTabs }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");

  const [user, setUser] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, _setActiveTab] = useState(defaultTab);
  const [followups, setFollowups] = useState([]);
  const [card, setCard] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(false);

  // Wrap the state setter so that clicking a tab also updates the URL
  // (?tab=xxx). Two-way sync: URL <-> state.
  const setActiveTab = useCallback((next) => {
    _setActiveTab(next);
    if (validTabs.has(next)) {
      const params = new URLSearchParams(searchParams);
      params.set("tab", next);
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams, validTabs]);

  useEffect(() => {
    (async () => {
      try {
        const [uR, pR] = await Promise.all([
          fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/company-profile`, { credentials: "include" }),
        ]);
        if (uR.ok) setUser(await uR.json());
        if (pR.ok) { const d = await pR.json(); setCompanyProfile(d.profile); }
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (urlTab && validTabs.has(urlTab)) _setActiveTab(urlTab);
  }, [urlTab, validTabs]);

  const fetchProgress = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/strategy/${endpointKey}/progress`, { credentials: "include" });
      if (r.ok) return await r.json();
    } catch (e) { /* ignore */ }
    return null;
  }, [endpointKey]);

  const postProgress = useCallback(async (payload, showToast = false) => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/strategy/${endpointKey}/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (r.ok && showToast) toast.success("Progress saved");
      else if (!r.ok && showToast) toast.error("Failed to save");
    } catch (e) { if (showToast) toast.error("Failed to save"); }
    setSaving(false);
  }, [endpointKey]);

  const postGenerateCard = useCallback(async (body) => {
    setGeneratingCard(true);
    let out = null;
    try {
      const r = await fetch(`${BACKEND_URL}/api/strategy/${endpointKey}/generate-card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body || {}),
      });
      if (r.ok) {
        out = await r.json();
        setCard(out);
        setCardOpen(true);
        toast.success("Card ready");
      } else {
        toast.error("Failed to generate card");
      }
    } catch (e) { toast.error("Failed"); }
    setGeneratingCard(false);
    return out;
  }, [endpointKey]);

  return {
    // identity
    user, companyProfile, urlTab,
    // ui state
    loading, saving, activeTab, setActiveTab,
    // shared slices
    followups, setFollowups,
    card, setCard, cardOpen, setCardOpen,
    generatingCard,
    // HTTP helpers
    fetchProgress, postProgress, postGenerateCard,
  };
}
